import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import FeaturesSection from "../components/FeaturesSection";
import CTASection from "../components/CTASection";
import TestimonialsFaq from "../components/TestimonialsFaq";
import ServicesCarousel from "../components/ServicesCarousel";
import GallerySection from "../components/GallerySection";
import OfferMarquee from "../components/OfferMarquee";

const API_BASE = import.meta.env.VITE_API_URL || "https://www.thehexaplay.com/api";

/* ================= ICONS ================= */
const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 A19.5 19.5 0 0 1 4.13 12.6 a19.79 19.79 0 0 1-3.07-8.67 A2 2 0 0 1 3.04 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 a2 2 0 0 1-.45 2.11L8.09 9.91 a16 16 0 0 0 6 6l1.27-1.27 a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const CallIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 A19.5 19.5 0 0 1 4.13 12.6 a19.79 19.79 0 0 1-3.07-8.67 A2 2 0 0 1 3.04 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 a2 2 0 0 1-.45 2.11L8.09 9.91 a16 16 0 0 0 6 6l1.27-1.27 a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 A2 2 0 0 1 22 16.92z"/>
  </svg>
);

/* ================= HERO SECTION ================= */
const HeroSection = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    location: "",
    date: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    location: "",
    date: "",
  });

  const [showCalendar, setShowCalendar] = useState(false);
  const [viewMonth, setViewMonth] = useState(new Date());
  const calendarRef = React.useRef(null);
  const today = new Date().toISOString().split("T")[0];

  const openCalendar = () => {
    const base = form.date ? new Date(form.date + "T00:00:00") : new Date();
    setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setShowCalendar(true);
  };

  const prevMonth = (e) => {
    e.stopPropagation();
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };

  const nextMonth = (e) => {
    e.stopPropagation();
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };

  const selectDate = (day) => {
    const y = viewMonth.getFullYear();
    const m = String(viewMonth.getMonth() + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    const dateStr = `${y}-${m}-${d}`;
    setForm((f) => ({ ...f, date: dateStr }));
    if (errors.date) setErrors((er) => ({ ...er, date: "" }));
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDay = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayNames = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  const daysInMonth = getDaysInMonth(viewMonth);
  const firstDay = getFirstDay(viewMonth);
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const validateName = (name) => {
    if (!name.trim()) return "Name is required";
    if (!/^[A-Za-z\s]{2,50}$/.test(name)) return "Name should contain only letters";
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile) return "Mobile number is required";
    if (mobile.length !== 10) return "Mobile number must be 10 digits";
    if (!/^[6-9][0-9]{9}$/.test(mobile)) return "Please enter a valid mobile number";
    return "";
  };

  const validateLocation = (location) => {
    if (!location) return "Please select a location";
    return "";
  };

  const validateDate = (date) => {
    if (!date) return "Please select a date";
    if (date < today) return "Please select a future date";
    return "";
  };

  const handleNameChange = (e) => {
    const val = e.target.value.replace(/[^A-Za-z\s]/g, "");
    setForm((f) => ({ ...f, name: val }));
    if (errors.name) setErrors((er) => ({ ...er, name: "" }));
  };

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, mobile: val }));
    if (errors.mobile) setErrors((er) => ({ ...er, mobile: "" }));
  };

  const handleLocationChange = (e) => {
    setForm((f) => ({ ...f, location: e.target.value }));
    if (errors.location) setErrors((er) => ({ ...er, location: "" }));
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (value === "" || value >= today) {
      setForm((f) => ({ ...f, date: value }));
      if (errors.date) setErrors((er) => ({ ...er, date: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name:     validateName(form.name),
      mobile:   validateMobile(form.mobile),
      location: validateLocation(form.location),
      date:     validateDate(form.date),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((err) => err !== "")) {
      const firstError = document.querySelector(".input-row.error");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    /* 📤 1. Background Save to MongoDB (Syncs to CustomerDB 'Home' tab) */
    fetch(`${API_BASE}/inquiries`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "home",
        name: form.name,
        mobile: form.mobile,
        whatsapp: form.mobile,
        location: form.location,
        date: form.date, 
      }),
    }).catch(err => console.warn("DB Save Error:", err));

    /* 📤 2. Background Save to Google Sheets */
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      fetch(
        "https://script.google.com/macros/s/AKfycbztJS_ctC-CkPqs93FpIcFmAVFeuhrVed1pCYyIkG6o3E-9qjg4gPqniwch95mPx3W6/exec",
        { method: "POST", body: formData }
      );
    } catch (error) {
      console.error("Google Sheet Error", error);
    }

    /* 🚀 3. INSTANT NAVIGATION to Room Card */
    localStorage.setItem("bookingData", JSON.stringify(form));
    navigate("/RoomCard");
  };

  return (
    <section className="hero-section">
      <div className="hero-container">

        <div className="hero-text-content">
          <h1 className="hero-heading">
            India's First 3D Private Theatre 
            Venue & Gaming Experience.
          </h1>
          <p className="hero-sub">
            Book Your Perfect Celebration: Birthdays, Anniversaries, Date Nights & More!
          </p>
        </div>

        <div className="booking-card">
          <form onSubmit={handleSubmit} noValidate>
            <div className={`input-row ${errors.name ? "error" : ""}`}>
              <span className="input-icon-wrap"><UserIcon /></span>
              <span className="input-divider" />
              <input type="text" name="name" placeholder="Enter your full name" value={form.name} onChange={handleNameChange} maxLength="50" autoComplete="name"/>
            </div>
            {errors.name && <div className="error-message show">{errors.name}</div>}

            <div className={`input-row ${errors.mobile ? "error" : ""}`}>
              <span className="input-icon-wrap"><PhoneIcon /></span>
              <span className="input-divider" />
              <input type="tel" name="mobile" placeholder="Enter 10 digit mobile number" value={form.mobile} onChange={handleMobileChange} maxLength="10" inputMode="numeric" autoComplete="tel"/>
            </div>
            {errors.mobile && <div className="error-message show">{errors.mobile}</div>}

            <div className={`input-row select-row ${errors.location ? "error" : ""}`}>
              <span className="input-icon-wrap"><MapPinIcon /></span>
              <span className="input-divider" />
              <select name="location" value={form.location} onChange={handleLocationChange}>
                <option value="" disabled>Select your preferred location</option>
                <option value="Chennai - Navalur">Chennai - Navalur </option>
              </select>
              <span className="select-chevron"><ChevronDownIcon /></span>
            </div>
            {errors.location && <div className="error-message show">{errors.location}</div>}

            <div className={`input-row date-row ${errors.date ? "error" : ""}`} onClick={openCalendar}>
              <span className="input-icon-wrap"><CalendarIcon /></span>
              <span className="input-divider" />
              <div className="date-wrapper">
                {!form.date && <span className="date-placeholder"></span>}
                <input type="text" readOnly className="custom-date-input" placeholder={form.date ? new Date(form.date).toLocaleDateString('en-GB', {day:'2-digit', month:'short', year:'numeric'}) : "Select date"} onFocus={(e) => e.target.showPicker && e.target.showPicker()}/>
                <input type="date" name="date" min={today} value={form.date} onChange={handleDateChange} className="native-date-input" style={{display: 'none'}}/>
              </div>
            </div>
            {errors.date && <div className="error-message show">{errors.date}</div>}

            <button type="submit" className="book-btn">Book Now</button>
            <a href="tel:+919087775659" className="call-btn"><CallIcon />Book On Call</a>
          </form>
        </div>
      </div>

      {showCalendar && (
        <div className="calendar-overlay">
          <div className="calendar-card" ref={calendarRef} onClick={(e) => e.stopPropagation()}>
            <div className="calendar-header">
              <button type="button" className="cal-nav" onClick={prevMonth} aria-label="Previous month"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg></button>
              <span className="cal-month-title">{monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
              <button type="button" className="cal-nav" onClick={nextMonth} aria-label="Next month"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg></button>
            </div>
            <div className="cal-weekdays">{dayNames.map((d) => <div key={d} className="cal-weekday">{d}</div>)}</div>
            <div className="cal-days">
              {calendarDays.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} className="cal-day cal-empty"></div>;
                const y = viewMonth.getFullYear();
                const m = String(viewMonth.getMonth() + 1).padStart(2, "0");
                const dStr = String(day).padStart(2, "0");
                const dateStr = `${y}-${m}-${dStr}`;
                const isPast = dateStr < today;
                const isSelected = dateStr === form.date;
                return (
                  <div key={dateStr} className={`cal-day${isPast ? " cal-past" : ""}${isSelected ? " cal-selected" : ""}`} onClick={isPast ? undefined : () => selectDate(day)}>
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ================= HOME PAGE ================= */
const sliderImages = [
  "/images/fe.jpg",
  "/images/fe1.jpg",
  "/images/fe2.jpg",
];

const Home = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <OfferMarquee />
      <HeroSection />

      <section className="home-section">
        <h1 className="home-title">Why Celebrate at The H00exaPlay?</h1>
        <p className="home-subtitle">
          Private Theatre Celebrations with Exciting Gaming & Stunning Decorations!
        </p>
        <div className="home-content">
          <div className="home-left">
            <ul className="feature-list">
              <li><strong>India's First 3D Private Theatre</strong><p>Experience movies like never before with HD big screens...</p></li>
              <li><strong>Unique & Private Celebration Experience</strong><p>Enjoy your special moments in a fully private theatre space...</p></li>
              <li><strong>Premium Decor & Celebration Setups</strong><p>Choose from romantic themes, birthday decorations...</p></li>
              <li><strong>Free Game Coupons for Extra Fun</strong><p>Every Private Theatre booking includes complimentary game coupons...</p></li>
              <li><strong>Easy & Hassle-Free Booking</strong><p>Book your Private Theatre celebration at The HexaPlay in just a few clicks...</p></li>
            </ul>
            <a href="/"><button className="cta-btn">Book Your Celebration</button></a>
          </div>
          <div className="home-right">
            <div className="slider">
              {sliderImages.map((img, i) => (
                <img key={i} src={img} className={`slide ${i === current ? "active" : ""}`} alt={`HexaPlay Private Theatre ${i + 1}`}/>
              ))}
            </div>
            <div className="slider-dots">
              {sliderImages.map((_, i) => (
                <span key={i} className={`dot ${i === current ? "active" : ""}`} onClick={() => setCurrent(i)}/>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FeaturesSection />
      <CTASection />
      <TestimonialsFaq />
      <ServicesCarousel />
      <GallerySection />
    </>
  );
};

export default Home;